import InfoIcon from "@mui/icons-material/Info";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import {
  Chip,
  Divider,
  Link,
  Stack,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext.tsx";
import theme from "../../../styles/theme.ts";
import { compareTwoValues, getDiffDaysMessage } from "../../../utils/main.ts";
import VisitsDrawer from "../VisitsDrawer.tsx";

export default function VisitsInfo() {
  const { highlightDate } = useContext(ProjectContext)!;
  const encounterSortEnum = {
    diffDays: "diffDays",
    encounterNumber: "encounterNumber",
    priority: "priority",
    related: "isRelated",
  } as const;
  type EncounterSortTypes =
    (typeof encounterSortEnum)[keyof typeof encounterSortEnum];
  const [sortBy, setSortBy] = useState<EncounterSortTypes>(
    encounterSortEnum.diffDays,
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc" | undefined>("asc");
  const { encounters } = useContext(PatientContext)!;
  const [drawer, setDrawer] = useState(false);
  const [visitToLoad, setVisitToLoad] = useState<string>("");
  const [doInitialSorting, setDoInitialSorting] = useState(true);

  function openDrawer(visit: PatientEncounterSummary) {
    setVisitToLoad(visit.id);
    setDrawer(true);
  }

  function toggleDrawer() {
    setDrawer(false);
  }

  function sortByDiffDays() {
    let newSortDir: "asc" | "desc" | undefined;
    const newSortBy = encounterSortEnum.diffDays;
    if (sortBy !== encounterSortEnum.diffDays || sortDir === "asc") {
      newSortDir = "desc";
    } else {
      newSortDir = "asc";
    }
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    sortEncounters(encounters ?? [], newSortBy, newSortDir);
  }

  function sortByPriority() {
    let newSortDir: "asc" | "desc" | undefined;
    const newSortBy = encounterSortEnum.priority;
    if (sortBy !== encounterSortEnum.priority || sortDir === "desc") {
      newSortDir = "asc";
    } else {
      newSortDir = "desc";
    }
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    sortEncounters(encounters ?? [], newSortBy, newSortDir);
  }

  function sortByRelated() {
    let newSortDir: "asc" | "desc" | undefined;
    const newSortBy = encounterSortEnum.related;
    if (sortBy !== encounterSortEnum.related || sortDir === "desc") {
      newSortDir = "asc";
    } else {
      newSortDir = "desc";
    }
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    sortEncounters(encounters ?? [], newSortBy, newSortDir);
  }

  function getRelatedSortRank(val: boolean | null): number {
    // Ascending sort order: related, unrelated, undetermined
    if (val === true) return 0;
    if (val === false) return 1;
    return 2;
  }

  const sortEncounters = useCallback(
    (
      visits: PatientEncounterSummary[],
      sortColumn: EncounterSortTypes,
      sortDirection: "asc" | "desc" | undefined,
    ) => {
      if (sortColumn === encounterSortEnum.related) {
        return visits.sort(
          (a: PatientEncounterSummary, b: PatientEncounterSummary) => {
            const diff =
              getRelatedSortRank(b.isRelated) - getRelatedSortRank(a.isRelated);
            return sortDirection === "asc" ? -diff : diff;
          },
        );
      }
      if (sortColumn === encounterSortEnum.diffDays) {
        // Primary sort on DiffDay, secondary sort on encounterNumber
        // This keeps the diffDay ordering of encounters,
        // but fixes the encounterNumber being out of order on the same DiffDay
        // TODO: Fix the encounterNumber to align with diffDay
        return visits.sort((a, b) => {
          let diffDaysResults, visitIdResults;
          if (sortDirection === "asc") {
            diffDaysResults = compareTwoValues(a.diffDays, b.diffDays);
            visitIdResults = compareTwoValues(
              a.encounterNumber,
              b.encounterNumber,
            );
          } else {
            diffDaysResults = compareTwoValues(b.diffDays, a.diffDays);
            visitIdResults = compareTwoValues(
              b.encounterNumber,
              a.encounterNumber,
            );
          }
          return diffDaysResults !== 0 ? diffDaysResults : visitIdResults;
        });
      }

      return visits.sort((a, b) => {
        if (sortDirection === "asc") {
          return compareTwoValues(a[sortColumn], b[sortColumn]);
        }
        return compareTwoValues(b[sortColumn], a[sortColumn]);
      });
    },
    [encounterSortEnum.related, encounterSortEnum.diffDays],
  );

  // Ensure that the encounter list is sorted when the component loads
  useEffect(() => {
    if (doInitialSorting && encounters) {
      sortEncounters(encounters, sortBy, "asc");
      setDoInitialSorting(false);
    }
  }, [
    doInitialSorting,
    setDoInitialSorting,
    encounters,
    sortEncounters,
    sortBy,
  ]);

  return (
    <>
      {drawer && (
        <VisitsDrawer
          toggleDrawer={() => toggleDrawer()}
          initialVisitId={visitToLoad}
        />
      )}
      <div className="visits-header">
        <div className="visits-row-date">
          <TableSortLabel
            active={sortBy === encounterSortEnum.diffDays}
            direction={sortDir}
            onClick={sortByDiffDays}
          >
            <Typography>Date </Typography>
          </TableSortLabel>
        </div>
        <div className="visits-row">
          <Divider orientation="vertical" flexItem />
          <TableSortLabel
            active={sortBy === encounterSortEnum.priority}
            direction={sortDir}
            onClick={sortByPriority}
          >
            <Typography className="left-divider">Priority</Typography>
          </TableSortLabel>
          <Tooltip
            title="Visits with a priority icon may include evidence of an adverse event, clinical or performance failure as identified by the 3Aware system."
            placement="top-end"
          >
            <InfoIcon sx={{ color: theme.palette.info.main }} />
          </Tooltip>
          <Divider orientation="vertical" flexItem className="right-divider" />
        </div>
        <div className="visits-row">
          <TableSortLabel
            active={sortBy === encounterSortEnum.related}
            direction={sortDir}
            onClick={sortByRelated}
          >
            <Typography className="left-divider">Related </Typography>
          </TableSortLabel>
        </div>
      </div>
      <Divider />
      <Stack>
        {encounters?.map((visit) => (
          <div key={visit.id}>
            <div
              className={"visit-item"}
              style={{
                backgroundColor:
                  highlightDate && new Date(visit.createdAt) >= highlightDate
                    ? theme.palette.highlight?.main
                    : undefined,
              }}
            >
              <div className={"visit-data"}>
                {visit.indexProcedure === true ? (
                  <Link
                    className="index-procedure"
                    component="button"
                    variant="subtitle2"
                    onClick={() => {
                      openDrawer(visit);
                    }}
                  >
                    Index
                  </Link>
                ) : (
                  <Link
                    className="post-day-index"
                    component="button"
                    variant="subtitle2"
                    onClick={() => {
                      openDrawer(visit);
                    }}
                  >
                    {getDiffDaysMessage(visit.diffDays)}
                  </Link>
                )}
                {visit.encounterType && visit.encounterNumber && (
                  <>
                    <div className="visit-type">
                      <Typography
                        className="label"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {"Visit ID: "}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {visit.encounterNumber}
                      </Typography>
                    </div>
                    <div className="visit-type">
                      <Typography
                        className="label"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {"Type: "}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {visit.encounterType}
                      </Typography>
                    </div>
                  </>
                )}
              </div>
              <div className="visit-icon-box">
                {visit && visit.priority > 0 && (
                  <OfflineBoltOutlinedIcon
                    sx={{ color: theme.palette.info.main }}
                  />
                )}
              </div>
              <div className="visit-actions">
                {visit.isRelated !== null && (
                  <Chip
                    label={visit.isRelated ? "Related" : "Unrelated"}
                    size="small"
                    sx={{
                      borderRadius: 3,
                      color: "common.white",
                      bgcolor: visit.isRelated ? green[600] : red[600],
                    }}
                    onDelete={() => undefined}
                    deleteIcon={
                      <Tooltip title={visit.reason}>
                        <StickyNote2OutlinedIcon
                          fontSize="small"
                          style={{ color: "#F0F0F0" }}
                        />
                      </Tooltip>
                    }
                  />
                )}
              </div>
            </div>
            <Divider />
          </div>
        ))}
      </Stack>
    </>
  );
}
